import { streamSSE } from "@/lib/stream-sse";

import type {
  ConversationMessage,
  FileAttachment,
  PatientSummaryContext,
} from "@/types/ai";

import { kyInstance } from "@/api/api-config";

/**
 * Streams AI-generated patient summary from the backend.
 * Uses Gemini's native structured JSON streaming which guarantees valid JSON output.
 * Citations are validated against the patient context on the backend.
 */
export async function* streamPatientSummary(
  context: PatientSummaryContext,
  signal?: AbortSignal,
): AsyncGenerator<string | { type: "validated"; text: string }, void, unknown> {
  const response = await kyInstance.post("ai/patient/summary", {
    json: { context },
    signal,
  });

  for await (const chunk of streamSSE(response, {
    signal,
    errorMessage: "Failed to generate patient summary",
  })) {
    if (typeof chunk === "string") {
      yield chunk;
    } else if (chunk.type === "validated") {
      // Backend validated the response and sent corrected version
      yield chunk;
    }
  }
}

/**
 * Streams AI-generated patient Q&A response from the backend.
 * Yields text chunks or summary events.
 */
export async function* streamPatientQA(
  context: PatientSummaryContext,
  question: string,
  conversationHistory?: ConversationMessage[],
  files?: FileAttachment[],
  signal?: AbortSignal,
  modelName: string = "gemini-3.1-flash-lite",
  patientId?: string,
  chatId?: string,
  threadId?: string,
): AsyncGenerator<
  | string
  | { type: "summary"; summary: string }
  | { type: "metadata"; chat_id: string }
  | { type: "validated"; text: string }
  | { type: "tool_call"; label: string },
  void,
  unknown
> {
  const response = await kyInstance.post("ai/patient/qa", {
    json: {
      context,
      question,
      conversationHistory: conversationHistory,
      files: files,
      modelName: modelName,
      patient_id: patientId,
      chat_id: chatId,
      thread_id: threadId,
    },
    signal,
  });

  // Yield all events including metadata and summaries
  for await (const chunk of streamSSE(response, {
    signal,
    errorMessage: "Failed to generate patient Q&A",
    yieldSummaryEvents: true,
    yieldToolCallEvents: true,
  })) {
    yield chunk;
  }
}

/**
 * Streams AI-generated patient suggestions from the backend.
 */
export async function* streamPatientSuggestions(
  context: PatientSummaryContext,
  conversationHistory?: ConversationMessage[],
  signal?: AbortSignal,
): AsyncGenerator<string, void, unknown> {
  const response = await kyInstance.post("ai/patient/suggestions", {
    json: {
      context,
      conversationHistory: conversationHistory,
    },
    signal,
  });

  for await (const chunk of streamSSE(response, {
    signal,
    errorMessage: "Failed to generate patient suggestions",
  })) {
    // Filter out summary events, only yield strings
    if (typeof chunk === "string") {
      yield chunk;
    }
  }
}

/**
 * Helper to parse suggestions from stream response.
 */
export async function generatePatientSuggestions(
  context: PatientSummaryContext,
  conversationHistory: ConversationMessage[],
): Promise<string[]> {
  let fullText = "";
  for await (const chunk of streamPatientSuggestions(
    context,
    conversationHistory.length > 0 ? conversationHistory : undefined,
  )) {
    fullText += chunk;
  }

  // Parse as JSON array
  let cleanedText = fullText.trim();
  cleanedText = cleanedText.replace(/```json\n?/g, "").replace(/```\n?/g, "");
  const parsed = JSON.parse(cleanedText);

  if (!Array.isArray(parsed)) {
    throw new Error("Expected JSON array from AI suggestions");
  }

  return parsed
    .map((s: unknown) => String(s).trim())
    .filter((s: string) => s.length > 0 && s.length < 100)
    .slice(0, 4);
}
